import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import AuthCard from "../components/AuthCard";

const COPY = {
  login: {
    title: "Welcome back",
    subtitle: "Log in to pick up where you left off.",
    submit: "Log in",
    switchText: "New to Plateo?",
    switchLink: "Create an account",
    switchTo: "/register",
  },
  register: {
    title: "Create your account",
    subtitle: "Track every application, write cover letters faster and never lose a recruiter's email.",
    submit: "Create account",
    switchText: "Already have an account?",
    switchLink: "Log in",
    switchTo: "/login",
  },
};

function AuthPage({ mode }) {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const copy = COPY[mode];
  const isRegister = mode === "register";

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      if (isRegister) {
        if (!acceptedTerms) {
          setError("Please agree to the Terms of Service and Privacy Policy to create your account.");
          setSubmitting(false);
          return;
        }
        const { email, mailConfigured } = await register(form.name, form.email, form.password, acceptedTerms);
        return navigate("/check-email", { state: { email, mailConfigured } });
      }
      await login(form.email, form.password);
      navigate(location.state?.from || "/", { replace: true });
    } catch (err) {
      if (err.code === "email_unverified") {
        return navigate("/check-email", { state: { email: form.email.trim(), unconfirmed: true } });
      }
      setError(err.message);
      setSubmitting(false);
    }
  };

  return (
    <AuthCard
      title={copy.title}
      subtitle={copy.subtitle}
      footer={
        <>
          {copy.switchText}{" "}
          <Link to={copy.switchTo} state={location.state}>
            {copy.switchLink}
          </Link>
        </>
      }
    >
      <form onSubmit={submit} noValidate>
        {error && (
          <div className="alert alert-danger py-2 small" role="alert">
            {error}
          </div>
        )}
        {isRegister && (
          <div className="mb-3">
            <label className="form-label small fw-medium" htmlFor="auth-name">
              Full name
            </label>
            <input
              id="auth-name"
              className="form-control"
              autoComplete="name"
              value={form.name}
              onChange={set("name")}
              autoFocus
              required
            />
          </div>
        )}
        <div className="mb-3">
          <label className="form-label small fw-medium" htmlFor="auth-email">
            Email
          </label>
          <input
            id="auth-email"
            type="email"
            className="form-control"
            autoComplete="email"
            value={form.email}
            onChange={set("email")}
            autoFocus={!isRegister}
            required
          />
        </div>
        <div className="mb-4">
          <div className="d-flex justify-content-between align-items-baseline">
            <label className="form-label small fw-medium" htmlFor="auth-password">
              Password
            </label>
            {!isRegister && (
              <Link to="/forgot-password" state={{ email: form.email }} className="small">
                Forgot password?
              </Link>
            )}
          </div>
          <input
            id="auth-password"
            type="password"
            className="form-control"
            autoComplete={isRegister ? "new-password" : "current-password"}
            value={form.password}
            onChange={set("password")}
            minLength={isRegister ? 8 : undefined}
            required
          />
          {isRegister && <div className="form-text">At least 8 characters.</div>}
        </div>
        {isRegister && (
          <div className="form-check mb-4">
            <input
              id="auth-accept-terms"
              className="form-check-input"
              type="checkbox"
              checked={acceptedTerms}
              onChange={(e) => setAcceptedTerms(e.target.checked)}
              required
            />
            <label className="form-check-label small" htmlFor="auth-accept-terms">
              I have read and agree to the{" "}
              <Link to="/terms" target="_blank">
                Terms of Service
              </Link>{" "}
              and{" "}
              <Link to="/privacy" target="_blank">
                Privacy Policy
              </Link>
              .
            </label>
          </div>
        )}
        <button type="submit" className="btn btn-primary w-100" disabled={submitting || (isRegister && !acceptedTerms)}>
          {submitting && <span className="spinner-border spinner-border-sm me-2" />}
          {copy.submit}
        </button>
      </form>
    </AuthCard>
  );
}

export default AuthPage;
